import Image from 'next/image'
import { useRouter } from 'next/router'

import type { ButtonProps } from '@wartek-id/button-v1'
import { Button } from '@wartek-id/button-v1'
import { Card } from '@wartek-id/card'
import { Text } from '@wartek-id/text-v1'

import { useAPIUpdateState } from 'api/kinerja/useAPIKinerja'
import { formatDate, isDateValid } from 'utils/datetime'
import useSKPTrackEvent from '../hooks/useSKPTrackEvent'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import {
  getPelaksanaanDate,
  isDateStarted,
} from 'app/KinerjaShared/utils/pelaksanaan'

import type { Kinerja, KinerjaStatus } from 'types/kinerja'
interface CardKinerjaStatusProps {
  skpId: string
  status: KinerjaStatus
  onSuccessUpdateSKP: () => any
}

export const getStatusMap = (
  period: Kinerja['period'],
  isPelaksanaanDatePassed: boolean,
  pelaksanaanDate: string
): Record<
  KinerjaStatus,
  {
    icon: string
    pretitle: string
    title: string
    description: string | JSX.Element
    ctaText?: string
    ctaButtonColor?: ButtonProps['color']
    ctaHref?: string
  }
> => {
  let pretitle: string, pelaksanaanMonth: string

  if (period === 1) {
    pretitle = '1-31 Januari'
  } else if (period === 2) {
    pretitle = '1-31 Juli'
  }

  if (isDateValid(pelaksanaanDate)) {
    pelaksanaanMonth = formatDate(pelaksanaanDate, 'MMMM yyyy')
  } else {
    pelaksanaanMonth = period === 1 ? 'Februari' : 'Agustus'
  }

  return {
    NOT_FOUND: {
      icon: 'img-to-do-list-document.png',
      pretitle,
      title: 'Mulai Perencanaan Kinerja',
      description:
        'Isi Praktik Pembelajaran, Pengembangan Kompetensi, Tugas Tambahan, dan Perilaku Kerja. Pastikan perencanaan diajukan sebelum batas waktu.',
      ctaText: 'Mulai',
      ctaButtonColor: 'blue',
      ctaHref: `${PENGELOLAAN_KINERJA_GURU_PATH}/konfirmasi-profil`,
    },
    DRAFT: {
      icon: 'img-to-do-list-document.png',
      pretitle,
      title: 'Isi Perencanaan Kinerja',
      description: 'Anda dapat memulai perencanaan kinerja',
      ctaText: 'Lanjut Rencanakan Kinerja',
      ctaButtonColor: 'blue',
      ctaHref: `${PENGELOLAAN_KINERJA_GURU_PATH}/perencanaan`,
    },
    WAITING_APPROVAL: {
      icon: 'img-time-document.png',
      pretitle,
      title: 'Menunggu persetujuan atasan',
      description:
        'Perencanaan Kinerja Anda sudah diajukan dan sedang diperiksa atasan. Persetujuan atasan akan muncul di halaman ini. Cek lagi nanti, ya.',
      ctaText: 'Lihat Rencana Kinerja',
      ctaButtonColor: 'white',
      ctaHref: `${PENGELOLAAN_KINERJA_GURU_PATH}/perencanaan`,
    },
    APPROVED: {
      icon: 'img-handshake.png',
      pretitle,
      title: 'Sepakati Perencanaan Kinerja dari atasan',
      description:
        'Perencanaan Kinerja Anda sudah disetujui. Mari cek jika ada penyesuaian dan sepakati untuk lanjut ke Pelaksanaan Kinerja.',
      ctaText: 'Cek dan Sepakati',
      ctaButtonColor: 'blue',
      ctaHref: `${PENGELOLAAN_KINERJA_GURU_PATH}/perencanaan`,
    },
    REVISED: {
      icon: 'img-time-document.png',
      pretitle,
      title: 'Menunggu persetujuan atasan',
      description:
        'Perencanaan Kinerja Anda sudah diajukan dan sedang diperiksa atasan. Persetujuan atasan akan muncul di halaman ini. Cek lagi nanti, ya.',
      ctaText: 'Lihat Rencana Kinerja',
      ctaButtonColor: 'white',
      ctaHref: `${PENGELOLAAN_KINERJA_GURU_PATH}/perencanaan`,
    },
    READY: isPelaksanaanDatePassed
      ? {
          icon: 'img-save-document-drive.png',
          pretitle,
          title: 'Kumpulkan Bukti Dukung Anda',
          description:
            'Bukti Dukung bisa dikumpulkan selama Penilaian Kinerja belum dilakukan oleh atasan.',
          ctaText: 'Lanjut ke Penilaian Kinerja',
          ctaButtonColor: 'blue',
        }
      : {
          icon: 'img-time-document.png',
          pretitle,
          title: 'Menunggu periode Pelaksanaan Kinerja',
          description: (
            <>
              Perencanaan Anda sudah selesai dan disepakati. Anda dapat lanjut
              ke Pelaksanaan mulai dari <strong>{pelaksanaanMonth}</strong>. Cek
              lagi halaman ini nanti, ya.
            </>
          ),
          ctaText: 'Cek Perencanaan Kinerja',
          ctaButtonColor: 'white',
          ctaHref: `${PENGELOLAAN_KINERJA_GURU_PATH}/perencanaan`,
        },
    WAITING_EVALUATION: {
      icon: 'img-numeric-mathematical.png',
      pretitle,
      title: 'Menunggu Penilaian Kinerja dari atasan',
      description:
        'Atasan sedang menilai seluruh Hasil Kerja Anda. Nanti nilainya akan muncul di sini. Ditunggu, ya.',
    },
  }
}

export default function CardKinerjaStatus({
  skpId,
  status,
  onSuccessUpdateSKP,
}: CardKinerjaStatusProps) {
  const {
    isEnabled: isMultplePeriodEnabled,
    isHavePeriod,
    periods,
    period: selectedPeriod,
  } = usePeriodContext()

  const isResolved = Array.isArray(periods) && periods.length > 0
  const period = selectedPeriod.period
  const periodeKinerja = selectedPeriod.periodeKinerja

  const pelaksanaanDate = getPelaksanaanDate(periodeKinerja)
  const isPelaksanaanDatePassed = isResolved && isDateStarted(periodeKinerja)

  const {
    icon: SelectedIcon,
    pretitle,
    title,
    description,
    ctaText,
    ctaButtonColor,
    ctaHref,
  } = getStatusMap(period, isPelaksanaanDatePassed, pelaksanaanDate)[status]

  const { mutateAsync: updateStateSKP, isLoading: isUpdatingStateSKP } =
    useAPIUpdateState()

  const isReadyForPelaksanaan = status === 'READY' && isPelaksanaanDatePassed
  const isDisabledCta = isReadyForPelaksanaan

  const router = useRouter()
  const { trackEvent } = useSKPTrackEvent()

  const handleCta = async () => {
    if (status === 'NOT_FOUND') {
      trackEvent({
        event_name: 'CLICK_KINERJA_START_PLANNING',
        screen_name: 'KINERJA_HOMEPAGE',
      })
    }

    if (isReadyForPelaksanaan) {
      await updateStateSKP(
        {
          skpId,
        },
        {
          onSuccess() {
            onSuccessUpdateSKP()
          },
        }
      )
    } else {
      router.push(ctaHref)
    }
  }

  return (
    <Card>
      <div className="flex gap-6 p-6">
        <div className="w-[84px] h-[84px] relative">
          {/* Prevent user select */}
          <div className="absolute inset-0 z-10">&nbsp;</div>

          <Image
            alt={title}
            src={['/illustrations', SelectedIcon].join('/')}
            width={84}
            height={84}
          />
        </div>
        <div className="flex-1 ml-6 flex flex-col gap-1 justify-center">
          <Text
            variant="subheading"
            color="informational"
            className="!text-blue-60 !uppercase tracking-[0.5px]"
          >
            {pretitle}
          </Text>
          <Text variant="heading-lg">{title}</Text>
          <Text color="subdued">{description}</Text>
        </div>
        {ctaText && (
          <div className="min-w-[240px]">
            <Button
              size="lg"
              fullWidth
              color={ctaButtonColor}
              disabled={
                isDisabledCta ||
                isUpdatingStateSKP ||
                (isMultplePeriodEnabled && !isHavePeriod)
              }
              isLoading={isUpdatingStateSKP}
              onClick={handleCta}
              data-testid="cta-button"
            >
              {ctaText}
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
}
